import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  effect,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { BdLoaderComponent, BdLoaderTemplate } from '@bindoc-projects/ui-lib/feedback/ui';
import { BdLoaderState } from '@bindoc-projects/platform-lib/data';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import { BdFeaturePermissionService } from '../core/feature-permissions.service';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'bd-feature-permission-checker',
  template: `
    <bd-loader
      [loadItem]="loading$"
      [successTemplate]="permissionCheckSuccessful"
      [errorTemplate]="permissionCheckFailed"
    >
    </bd-loader>

    <ng-template #permissionCheckSuccessful>
      <ng-content></ng-content>
    </ng-template>

    <ng-template #permissionCheckFailed>
      <ng-container *ngTemplateOutlet="errorTemplate()"> </ng-container>
    </ng-template>
  `,
  styles: [``],
  imports: [BdLoaderComponent, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BdFeaturePermissionCheckerComponent {
  readonly permissions = input<`${string}:${string}`[]>([]);
  readonly errorTemplate = input<TemplateRef<BdLoaderTemplate>>();

  public loading$: BehaviorSubject<BdLoaderState> =
    new BehaviorSubject<BdLoaderState>(BdLoaderState.INIT);

  private readonly _permissionsModulesService = inject(
    BdFeaturePermissionService,
  );
  private readonly _authService = inject(BdAuthenticationService);

  /**
   * The Klinik-Browser (and other public surfaces) render permission-gated
   * blocks — e.g. the contact-person details — without requiring a signed-in
   * user. Firing the permission lookup while anonymous would hit an
   * authenticated endpoint and can trigger a B2C login redirect, so the checker
   * only runs once MSAL confirms a session and otherwise fails silent: the
   * gated content simply stays hidden, with no request, error or notification.
   *
   * Backed by a `BehaviorSubject(false)`, so this is `false` until MSAL
   * confirms the session; authenticated apps flip it to `true` and behave
   * exactly as before.
   */
  private readonly _isAuthenticated = toSignal(
    this._authService.authentication$,
    { initialValue: false },
  );

  constructor() {
    effect(() => {
      const perms = this.permissions();

      // Fail silent on public pages: no session means no permission, so hide
      // the gated content without contacting the authenticated endpoint.
      if (!this._isAuthenticated()) {
        this.loading$.next(BdLoaderState.ERROR);
        return;
      }

      if (perms && perms.length > 0) {
        this.loading$.next(BdLoaderState.LOADING);
        this._permissionsModulesService.containsPermission(perms).subscribe({
          next: (checkSuccessful) => {
            this.loading$.next(
              checkSuccessful ? BdLoaderState.SUCCESS : BdLoaderState.ERROR,
            );
          },
          error: () => {
            this.loading$.next(BdLoaderState.ERROR);
          },
        });
      }
    });
  }
}
